import { translateTemplateLevelText, translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ListAchievement, ListQuest } from '../../client/types';
import { calculateAchievementProgress } from '../../helpers/achievement';
import { toDueDate } from '../../helpers/datetime';
import { CommonIcon } from '../common/CommonIcon';
import { AchievementItem } from './AchievementItem';
import { BorderLinearProgress } from './BorderLinearProgress';
import { AchievementProgressSkeleton } from './skeleton/AchievementProgressSkeleton';

const ROW_DIVIDER_BORDER = '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)';

export type AchievementListItemProps = {
  achievement: ListAchievement;
  quests: ListQuest[];
  questsLoading?: boolean;
  showDivider?: boolean;
  onClick: () => void;
};

export const AchievementListItem: FC<AchievementListItemProps> = ({
  achievement,
  quests,
  questsLoading,
  showDivider = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const progress = calculateAchievementProgress(achievement, quests);
  const isProgressLoading = !progress.external && !achievement.achievedOn && questsLoading;
  const { percentage, completed, total } = progress;
  const hasMultipleLevels = (achievement.achievementLevels?.length ?? 0) > 1;
  const totalLevels = achievement.achievementLevels?.length ?? 0;
  const isMaxLevelReached = !!progress.external && totalLevels > 0 && progress.external.level >= totalLevels;
  const displayLevel = progress.external?.displayedLevel;
  const displayLevelIndex = progress.external ? Math.max(progress.external.level - 1, 0) : null;
  const displayName = displayLevel
    ? translateTemplateLevelText(t, achievement.templateId, displayLevelIndex, 'name', displayLevel.name)
    : translateTemplateText(t, achievement.templateId, 'name', achievement.name);
  const displayDescription = displayLevel
    ? translateTemplateLevelText(t, achievement.templateId, displayLevelIndex, 'description', displayLevel.description)
    : translateTemplateText(t, achievement.templateId, 'description', achievement.description ?? '');

  const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick: onPressClick } = useLiquidGlassPress({
    preventDefault: true,
    onActivate: () => {
      hapticFeedback.impactOccurred.ifAvailable('light');
      onClick();
    },
  });

  return (
    <Box
      component="button"
      type="button"
      aria-label={displayName}
      onClick={onPressClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      sx={{
        display: 'block',
        width: '100%',
        m: 0,
        px: '14px',
        py: '12px',
        border: 'none',
        borderTop: showDivider ? ROW_DIVIDER_BORDER : 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
        transition: 'background-color 0.12s ease',
      }}
    >
      <Stack direction="row" gap="12px" alignItems="flex-start">
        <Stack alignItems="center" spacing={0.25} sx={{ width: 60, flexShrink: 0 }}>
          <Box sx={{ width: 60, height: 80, filter: achievement.achievedOn ? 'none' : 'grayscale(100%)' }}>
            <AchievementItem
              icon={achievement.icon}
              title=""
              rarity={achievement.rarity}
              iconImageColor={displayLevel?.iconImageColor ?? achievement.iconImageColor}
              iconImageUrl={displayLevel?.iconImageUrl ?? achievement.iconImageUrl}
            />
          </Box>
          {hasMultipleLevels && progress.external && (
            <Text style={{ color: achievement.achievedOn ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)', fontSize: '10px', whiteSpace: 'nowrap' }}>
              {t('achievementEditor.level', { count: progress.external.level })}
            </Text>
          )}
        </Stack>

        <Stack sx={{ flex: 1, minWidth: 0 }} gap="6px" pt="2px">
          <Text weight="2" style={{ fontSize: '16px', color: 'var(--tg-theme-text-color)' }}>
            {displayName}
          </Text>
          {displayDescription && (
            <Text style={{ fontSize: '13px', color: 'var(--tg-theme-hint-color)' }}>
              {displayDescription}
            </Text>
          )}

          {progress.external && !isMaxLevelReached ? (
            <Stack direction="column" gap="6px" width="100%">
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Text style={{ fontSize: '14px', color: 'var(--tg-theme-hint-color)' }}>
                  {completed}/{total}
                </Text>
                <Text style={{ fontSize: '14px', color: 'var(--tg-theme-hint-color)' }}>
                  {percentage}%
                </Text>
              </Stack>
              <BorderLinearProgress
                variant="determinate"
                value={percentage}
                sx={{ width: '100%', height: '15px' }}
              />
              {progress.external.nextLevel && (
                <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                  {t('achievementEditor.nextLevel', {
                    name: translateTemplateLevelText(t, achievement.templateId, progress.external.level, 'name', progress.external.nextLevel.name),
                    description: translateTemplateLevelText(t, achievement.templateId, progress.external.level, 'description', progress.external.nextLevel.description),
                  })}
                </Text>
              )}
            </Stack>
          ) : achievement.achievedOn ? (
            <Stack direction="row" alignItems="center" gap="2px">
              <CommonIcon
                icon="emoji_events"
                color="var(--tg-theme-button-color)"
                size={16}
                type="filled"
              />
              <Text weight='2' style={{
                fontSize: '14px',
                color: 'var(--tg-theme-button-color)'
              }}>
                {t('achievementEditor.achieved', { date: toDueDate(dayjs(achievement.achievedOn)) })}
              </Text>
            </Stack>
          ) : isProgressLoading ? (
            <AchievementProgressSkeleton />
          ) : (
            <Stack direction="column" gap="2px" width="100%">
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Text style={{ fontSize: '14px', color: 'var(--tg-theme-hint-color)' }}>
                  {completed}/{total}
                </Text>
                <Text style={{ fontSize: '14px', color: 'var(--tg-theme-hint-color)' }}>
                  {percentage}%
                </Text>
              </Stack>
              <BorderLinearProgress
                variant="determinate"
                value={percentage}
                sx={{ width: '100%', height: '15px' }}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
